import { MaskFormTextField } from '@exam-center/components';
import { GetBlockedUserByIdDocument, useUpdateBlockedUserMutation } from '@exam-center/graphql';
import { PHONE_NUMBER_MASK } from '@exam-center/utils';
import {
  Flex,
  Form,
  FormButton,
  Grid,
  PaperSection,
  ReadonlyField,
  SectionTitle,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

import {
  type BlockedUserFormInput,
  useBlockedUserDetails,
  useBlockedUserFormContext,
} from '../../providers';

export default function BlockedUserFormLogic() {
  const { blockedUserDetails } = useBlockedUserDetails();
  const { showPromiseSnackbar } = useSnackbar();

  const { idNumber, title, firstName, middleName, lastName, email } = blockedUserDetails;

  const formContext = useBlockedUserFormContext();

  const [updateBlockedUser, { loading: isUpdatingBlockedUser }] = useUpdateBlockedUserMutation();

  const handleUpdateBlockedUser = async (data: BlockedUserFormInput) => {
    return await updateBlockedUser({
      variables: {
        updateBlockedUserInput: {
          blockedUserId: blockedUserDetails.id,
          info: {
            phoneNumber: data.phoneNumber,
          },
        },
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetBlockedUserByIdDocument],
    });
  };

  const handleSubmit = async (data: BlockedUserFormInput) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateBlockedUser(data),
    });
  };

  const isSubmittingForm = formContext.formState.isSubmitting || isUpdatingBlockedUser;

  return (
    <Form
      onSubmit={formContext.handleSubmit(handleSubmit)}
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: theme.spacing(6),
      })}
    >
      <PaperSection title='ข้อมูลส่วนตัว'>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            p: theme.spacing(6),
          })}
        >
          <SectionTitle title='Personal Information' />
          <Grid
            sx={(theme) => ({
              gridTemplateColumns: '240px 1fr',
              gap: theme.spacing(6),
            })}
          >
            <Text variant='body1Semibold'>ข้อมูลส่วนตัว</Text>
            <Flex
              sx={(theme) => ({
                gap: theme.spacing(4),
                flexDirection: 'column',
              })}
            >
              <ReadonlyField
                label='ID / Passport No.'
                content={idNumber}
                sx={{ maxWidth: '600px' }}
              />
              <Flex
                sx={(theme) => ({
                  gap: theme.spacing(4),
                })}
              >
                <ReadonlyField
                  label='คำนำหน้า'
                  sx={{
                    maxWidth: '200px',
                  }}
                  content={title}
                />
                <ReadonlyField label='ชื่อ' content={firstName} />
                <ReadonlyField label='ชื่อกลาง' content={middleName || ''} />
                <ReadonlyField label='นามสกุล' content={lastName} />
              </Flex>
            </Flex>
            <Text variant='body1Semibold'>ข้อมูลสำหรับติดต่อ</Text>
            <Flex
              sx={(theme) => ({
                flexDirection: 'column',
                gap: theme.spacing(4),
                maxWidth: '600px',
              })}
            >
              <ReadonlyField label='อีเมลผู้ใช้' content={email ?? '-'} />
              <MaskFormTextField
                name='phoneNumber'
                label='เบอร์โทรศัพท์'
                placeholder='กรอกเบอร์โทรศัพท์'
                maskOptions={{
                  mask: PHONE_NUMBER_MASK,
                }}
              />
            </Flex>
          </Grid>
        </Flex>
      </PaperSection>
      <FormButton
        label='บันทึก'
        size='large'
        loading={isSubmittingForm}
        disabled={isSubmittingForm}
      />
    </Form>
  );
}
